/**
 * @fileoverview Menu Drawer Components
 *
 * `MenuDrawer` provides an accessible nested `<details>`-based navigation drawer
 * with focus trapping and submenu open/close animation. `HeaderDrawer` extends it
 * with header-specific positioning and body scroll locking.
 *
 * @example
 * <menu-drawer data-breakpoint="mobile">
 *   <details>
 *     <summary>Menu</summary>
 *     <div>…nested nav…</div>
 *   </details>
 * </menu-drawer>
 */

import { trapFocus, removeTrapFocus, scrollLock } from '@utilities/theme';

/**
 * @classdesc Accessible nested navigation drawer built on `<details>`.
 * @extends HTMLElement
 */
export class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    if (navigator.platform === 'iPhone') document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    // Summaries with [data-opener-level] manage their own open/close + animation
    // (see header-dropdown.js) and must not also be driven by this generic handler.
    this.querySelectorAll('summary:not([data-opener-level])').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
  }

  onKeyUp(event) {
    if(event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if(!openDetailsElement) return;

    // Self-managed dropdowns (see header-dropdown.js) handle their own Escape key.
    if (openDetailsElement.querySelector(':scope > summary[data-opener-level]')) return;

    // A .child-menu-drawer panel is a <ul>, not a <details>, so the guard above
    // does not catch it: closest('details[open]') skips past the open panel and
    // resolves to the drawer's own <details>. Escape inside a submenu therefore
    // closed the whole drawer instead of just the panel.
    //
    // header-dropdown.js owns that case, but it closes the panel on keydown --
    // by the time this keyup handler runs the panel is gone and cannot be
    // detected. It sets this flag instead; consume it and stand down, so one
    // Escape closes the panel only. A second Escape finds no flag and closes the
    // drawer, which is the expected step-back-out behavior.
    if (document.documentElement.dataset.childPanelEscape) {
      delete document.documentElement.dataset.childPanelEscape;
      return;
    }

    openDetailsElement === this.mainDetailsToggle ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary')) : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if(isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        !reducedMotion || reducedMotion.matches ? addTrapFocus() : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach(details =>  {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle?.querySelector('summary')?.setAttribute('aria-expanded', false);
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);
  }

  onFocusOut(event) {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement)) this.closeMenuDrawer();
    });
  }

  closeSubmenu(detailsElement) {
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    }

    window.requestAnimationFrame(handleAnimation);
  }
}

if (!customElements.get('menu-drawer')) {
  customElements.define('menu-drawer', MenuDrawer);
}

/**
 * @classdesc Header navigation drawer with header positioning + body scroll lock.
 * @extends MenuDrawer
 */
export class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.getElementById('shopify-section-header');
    this.bar = this.bar || document.querySelector('announcement-bar');
    this.borderOffset = this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    if (this.bar) {
      document.documentElement.style.setProperty('--header-bottom-position', `${parseInt(this.bar.getBoundingClientRect().bottom - this.borderOffset, 10)}px`);
    }
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    scrollLock.enabledBodyScrollLock();
  }

  closeMenuDrawer(event, elementToFocus) {
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    scrollLock.disableBodyScrollLock();
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}
